/**
 * EditWorkdayModal — admin rättar en arbetsdag (Fas 3).
 * Kallar PATCH /workday/{id} med stamp_in / stamp_out / workplace / note.
 * Låst för godkända dagar (backend returnerar 409 → visas som toast).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PencilLine, Warning } from "@phosphor-icons/react";
import ModalPortal from "@/components/ModalPortal";

// Konverterar ISO-datum till lokalt "YYYY-MM-DDTHH:mm" för <input type="datetime-local">.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Lokalt "YYYY-MM-DDTHH:mm" → ISO med Z (behandlas som lokal tid → UTC).
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return d.toISOString();
}

export function EditWorkdayModal({ workday, onClose, onSaved }) {
  const [stampIn, setStampIn] = useState(isoToLocalInput(workday.stamp_in));
  const [stampOut, setStampOut] = useState(isoToLocalInput(workday.stamp_out));
  const [workplaceId, setWorkplaceId] = useState(workday.workplace_id || "");
  const [note, setNote] = useState(workday.note || "");
  const [workplaces, setWorkplaces] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadWps = async () => {
      try {
        const r = await api.get("/workday/workplaces");
        setWorkplaces((r.data || []).filter((w) => w.active !== false));
      } catch (e) {
        console.error(e);
      }
    };
    loadWps();
  }, []);

  const save = async () => {
    if (!stampIn) {
      toast.error("Stämplat in krävs");
      return;
    }
    if (workday.status === "completed" && !stampOut) {
      toast.error("Stämplat ut krävs för avslutade arbetsdagar");
      return;
    }
    if (stampOut && new Date(stampOut) <= new Date(stampIn)) {
      toast.error("Stämplat ut måste vara efter stämplat in");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      const origIn = isoToLocalInput(workday.stamp_in);
      const origOut = isoToLocalInput(workday.stamp_out);
      const origWp = workday.workplace_id || "";
      const origNote = workday.note || "";

      if (stampIn !== origIn) payload.stamp_in = localInputToIso(stampIn);
      if (stampOut !== origOut) payload.stamp_out = localInputToIso(stampOut);
      if (workplaceId !== origWp) payload.workplace_id = workplaceId || null;
      if (note !== origNote) payload.note = note;

      if (Object.keys(payload).length === 0) {
        toast.info("Inga ändringar att spara");
        setSaving(false);
        return;
      }

      const r = await api.patch(`/workday/${workday.workday_id}`, payload);
      toast.success("Arbetsdagen är rättad och loggförd");
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const isLocked = !!workday.approved_at;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="edit-workday-modal"
      >
        <div
          className="bg-surface border border-app rounded-2xl p-6 w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl flex items-center gap-2">
                <PencilLine size={22} weight="duotone" className="text-brand" />
                Rätta arbetsdag
              </h2>
              <p className="text-sm text-text-muted mt-1">
                {workday.user_name} · {(workday.stamp_in || "").slice(0, 10)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary"
              aria-label="Stäng"
              data-testid="edit-workday-close"
            >
              <X size={22} />
            </button>
          </div>

          {isLocked && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2 text-sm">
              <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-amber-900">
                Arbetsdagen är godkänd och låst. Häv attesten under <strong>Attest</strong> för att kunna rätta.
              </div>
            </div>
          )}

          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-text-muted">
                  Stämplat in
                </label>
                <input
                  type="datetime-local"
                  value={stampIn}
                  onChange={(e) => setStampIn(e.target.value)}
                  disabled={isLocked || saving}
                  className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-50"
                  data-testid="edit-workday-stamp-in"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-text-muted">
                  Stämplat ut
                </label>
                <input
                  type="datetime-local"
                  value={stampOut}
                  onChange={(e) => setStampOut(e.target.value)}
                  disabled={isLocked || saving}
                  className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-50"
                  data-testid="edit-workday-stamp-out"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-text-muted">
                Arbetsplats
              </label>
              <select
                value={workplaceId}
                onChange={(e) => setWorkplaceId(e.target.value)}
                disabled={isLocked || saving}
                className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-50"
                data-testid="edit-workday-workplace"
              >
                <option value="">Ingen arbetsplats</option>
                {workplaces.map((w) => (
                  <option key={w.workplace_id} value={w.workplace_id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-text-muted">
                Notering (visas i revisionsloggen)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isLocked || saving}
                rows={3}
                placeholder="T.ex. 'Rättade instämpling — hade glömt stämpla in kl 07:00'"
                className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-50"
                data-testid="edit-workday-note"
              />
            </div>
            <div className="text-xs text-text-muted bg-app/50 border border-app rounded-md p-3">
              💡 Raster räknas om automatiskt om du ändrar in-/ut-tid. Ändringen loggas med ditt namn och tidsstämpel i revisionsloggen (öppna via <em>Visa</em>-länken).
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="tk-btn-outline"
              data-testid="edit-workday-cancel"
            >
              Avbryt
            </button>
            <button
              onClick={save}
              disabled={saving || isLocked}
              className="tk-btn-primary"
              data-testid="edit-workday-save"
            >
              {saving ? "Sparar..." : "Spara ändring"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
